(() => {
  'use strict';

  const STORAGE_KEY = 'wishlist:v1';
  const PAGE_SELECTOR = '[data-wishlist-page]';
  const CARD_RENDERER_SECTION_ID = 'wishlist-product-card-renderer';
  const CARD_CACHE = new Map();
  let renderCounter = 0;

  function getWishlistItemHandle(item) {
    const url = item.url || '';
    const handleFromUrl = url ? String(url).split('?')[0].split('/products/')[1] || '' : '';

    return item.handle || item.productHandle || item.product_handle || handleFromUrl;
  }

  function readWishlist() {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) return [];

    try {
      const data = JSON.parse(raw);

      if (!data || !Array.isArray(data.items)) return [];

      return data.items
        .filter(Boolean)
        .map((item) => {
          const handle = getWishlistItemHandle(item);
          const url = item.url || '';

          return {
            key: handle ? `h:${handle}` : '',
            handle,
            url: url || (handle ? `/products/${handle}` : ''),
            variantId: item.variantId || item.variant_id || '',
          };
        })
        .filter((item) => item.handle);
    } catch (error) {
      console.warn('[wishlist-page] localStorage read failed', error);
      return [];
    }
  }

  function removeWishlistItems(itemsToRemove) {
    if (!itemsToRemove.length) return false;

    const keysToRemove = new Set(itemsToRemove.map((item) => item.key).filter(Boolean));
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw || !keysToRemove.size) return false;

    try {
      const data = JSON.parse(raw);

      if (!data || !Array.isArray(data.items)) return false;

      const nextItems = data.items.filter((item) => {
        const handle = getWishlistItemHandle(item);
        const key = handle ? `h:${handle}` : '';

        return !keysToRemove.has(key);
      });

      if (nextItems.length === data.items.length) return false;

      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: nextItems }));

      itemsToRemove.forEach((item) => {
        CARD_CACHE.delete(`${item.handle}:${item.variantId || ''}`);
      });

      window.norkayaWishlist?.sync?.();

      const detail = {
        count: nextItems.length,
        items: nextItems,
      };

      document.dispatchEvent(new CustomEvent('wishlist:changed', { detail }));
      document.dispatchEvent(new CustomEvent('wishlist:update', { detail }));

      return true;
    } catch (error) {
      console.warn('[wishlist-page] localStorage cleanup failed', error);
      return false;
    }
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.hidden = hidden;
  }

  function getProductsPerPage(page) {
    return Math.max(Number(page.dataset.wishlistProductsPerPage || 0), 1);
  }

  function refreshLoadMore(loadMore) {
    if (!loadMore) return;

    if (typeof loadMore.updateStaticState === 'function') {
      loadMore.updateStaticState();
      return;
    }

    customElements.whenDefined('collection-load-more').then(() => {
      if (typeof loadMore.updateStaticState === 'function') {
        loadMore.updateStaticState();
      }
    });
  }

  function getProductRequestUrl(item) {
    const baseUrl = new URL(`/products/${item.handle}`, window.location.origin);
    baseUrl.searchParams.set('section_id', CARD_RENDERER_SECTION_ID);

    if (item.variantId) {
      baseUrl.searchParams.set('variant', item.variantId);
    }

    return `${baseUrl.pathname}${baseUrl.search}`;
  }

  function normalizeRendererHtml(responseText) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const section = html.querySelector(`#shopify-section-${CARD_RENDERER_SECTION_ID}`);
    const productCard = section?.querySelector('product-card') || html.querySelector('product-card');

    if (!productCard) return '';

    return productCard.outerHTML;
  }

  async function fetchRenderedCard(item) {
    const cacheKey = `${item.handle}:${item.variantId || ''}`;

    if (CARD_CACHE.has(cacheKey)) {
      return CARD_CACHE.get(cacheKey);
    }

    const request = fetch(getProductRequestUrl(item), { credentials: 'same-origin' })
      .then((response) => {
        if (response.status === 404) {
          return {
            html: '',
            shouldRemove: true,
          };
        }

        if (!response.ok) throw new Error(`Failed to render wishlist card: ${item.handle}`);

        return response.text().then((responseText) => {
          const html = normalizeRendererHtml(responseText);

          return {
            html,
            shouldRemove: !html,
          };
        });
      })
      .catch((error) => {
        console.warn('[wishlist-page] card render failed', error);
        return {
          html: '',
          shouldRemove: false,
        };
      });

    CARD_CACHE.set(cacheKey, request);
    return request;
  }

  function syncInsertedCard(item, li) {
    if (!li) return;

    const wishlistButton = li.querySelector('[data-wishlist-button]');

    if (wishlistButton) {
      wishlistButton.classList.add('is-active');
      wishlistButton.setAttribute('aria-pressed', 'true');
      wishlistButton.setAttribute('aria-label', 'Remove from wishlist');

      if (!wishlistButton.dataset.productHandle) {
        wishlistButton.dataset.productHandle = item.handle;
      }
    }

    li.querySelectorAll('overflow-list[defer]').forEach((overflowList) => {
      overflowList.removeAttribute('defer');
    });
  }

  function renderFallbackCard(item, li) {
    li.innerHTML = `
      <article class="wishlist-page__fallback-card">
        <a class="wishlist-page__fallback-link" href="${item.url}">
          ${item.handle}
        </a>
      </article>
    `;
  }

  async function fillCard(item, li) {
    const result = await fetchRenderedCard(item);

    if (!li.isConnected) return null;

    if (result.shouldRemove) {
      li.remove();

      return {
        item,
        shouldRemove: true,
      };
    }

    if (!result.html) {
      renderFallbackCard(item, li);
      return null;
    }

    li.innerHTML = result.html;
    syncInsertedCard(item, li);

    document.dispatchEvent(new CustomEvent('wishlist:rendered-card', {
      detail: {
        item,
        card: li.querySelector('product-card'),
      },
    }));

    return null;
  }

  async function renderPage(page) {
    const grid = page.querySelector('[data-wishlist-page-grid]');
    const gridWrap = page.querySelector('[data-wishlist-page-grid-wrap]');
    const empty = page.querySelector('[data-wishlist-page-empty]');
    const loading = page.querySelector('[data-wishlist-page-loading]');
    const count = page.querySelector('[data-wishlist-page-count]');
    const template = page.querySelector('[data-wishlist-card-template]');
    const loadMore = page.querySelector('[data-wishlist-load-more]');

    if (!grid || !template) return;

    const items = readWishlist();
    const renderId = String(++renderCounter);
    const productsPerPage = getProductsPerPage(page);

    page.dataset.wishlistRenderId = renderId;
    grid.replaceChildren();
    setHidden(loadMore, true);
    setHidden(loading, false);

    if (!items.length) {
      setHidden(gridWrap, true);
      setHidden(loading, true);
      setHidden(empty, false);
      setHidden(count, true);
      refreshLoadMore(loadMore);
      return;
    }

    const fragment = document.createDocumentFragment();

    const cardPromises = [];

    items.forEach((item, index) => {
      const cardFragment = template.content.cloneNode(true);
      const li = cardFragment.querySelector('[data-wishlist-page-item]');

      if (!li) return;

      li.dataset.productHandle = item.handle;
      li.dataset.variantId = item.variantId || '';
      li.dataset.loadMoreItem = '';
      li.hidden = index >= productsPerPage;
      fragment.appendChild(cardFragment);

      cardPromises.push(fillCard(item, li));
    });

    grid.appendChild(fragment);

    setHidden(gridWrap, false);
    setHidden(empty, true);

    if (count) {
      count.textContent = `${items.length} ${items.length === 1 ? 'item' : 'items'}`;
      count.hidden = false;
    }

    if (loadMore) {
      const shownCount = Math.min(items.length, productsPerPage);

      loadMore.dataset.staticPageSize = String(productsPerPage);
      loadMore.dataset.shownCount = String(shownCount);
      loadMore.dataset.totalCount = String(items.length);
      refreshLoadMore(loadMore);
    }

    const cardResults = await Promise.allSettled(cardPromises);

    if (page.dataset.wishlistRenderId !== renderId) return;

    const removedItems = cardResults
      .filter((result) => result.status === 'fulfilled' && result.value?.shouldRemove)
      .map((result) => result.value.item);

    if (removeWishlistItems(removedItems)) return;

    setHidden(loading, true);
  }

  function initWishlistPage(root = document) {
    root.querySelectorAll(PAGE_SELECTOR).forEach((page) => {
      renderPage(page);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initWishlistPage());

  document.addEventListener('wishlist:changed', () => {
    initWishlistPage();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initWishlistPage(event.target);
  });

  window.addEventListener('pageshow', () => {
    initWishlistPage();
  });
})();